import {
	type BaseSyntheticEvent,
	type ForwardedRef,
	forwardRef,
	type JSX,
	type ReactNode,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
} from 'react'

import { superstructResolver } from '@hookform/resolvers/superstruct'
import {
	type CriteriaMode,
	type DefaultValues,
	type FieldValues,
	FormProvider,
	set,
	type UseFormReturn,
	useForm,
	type ValidationMode,
} from 'react-hook-form'
import type { CSSProperties } from 'styled-components'

import type { Struct } from '@subaio/validation'

import { FormOptionsProvider } from '../useFormOptions/Provider'
import { clearInputs, syncNamedElements } from './clearInputs'
import { inferFieldType } from './inferFieldType'
import { sanitizeFields } from './sanitizeFields'

export type FormMethods<T extends FieldValues> = UseFormReturn<T, object> & {
	/** Clear the value of all fields and reset the default values */
	clearFields: () => void
}
export type FormRenderFields<T extends FieldValues> = {
	[TKey in keyof T]-?: {
		name: keyof T
		isRequired: boolean
		type: 'email' | 'number' | 'password' | undefined
	}
}

export type FormProps<T extends FieldValues> = {
	/**
	 * Superstruct schema for validation. The schema is then in turn used by the `fieldProps` function to set the relevant
	 * type and validation for each field if applicable.
	 * @example
	 * ```tsx
	 * const schema = object({
	 * 	text: fieldString(),
	 * 	number: fieldNumber({ isOptional: true }),
	 * })
	 *
	 * <Form
	 * 	schema={schema}
	 * 	onSubmit={(fields) => console.log(fields)}
	 * 	render={({ text, number }) => (
	 * 		<>
	 * 			<Input {...text} label='Required string' />
	 * 			<Input {...number} label='Optional number' />
	 * 			<button type='submit'>Submit</button>
	 * 		</>
	 * 	)}
	 * />
	 * ```
	 */
	schema?: Struct<T, any>
	/** Avoid setting values to `undefined` intentionally. Prefer `null` instead if it has to be defined without a value. */
	initialValues?: DefaultValues<T>
	/** Changes when validation is done. The prop is described [here](https://react-hook-form.com/api/useform) */
	validationMode?: keyof ValidationMode
	/** Changes when validation is done **after** the user has submitted. The prop is described [here](https://react-hook-form.com/api/useform) */
	reValidateMode?: 'onBlur' | 'onChange' | 'onSubmit'
	/** Decided if it is only the first error from each field that will be gathered or all of their errors */
	criteriaMode?: CriteriaMode
	/** Whether or not to focus the first error in the form if any */
	shouldFocusError?: boolean
	/** Toggles the native HTML validation on or off */
	isNativeValidation?: boolean
	/** Informs all underlying fields to show the changed indicator when the field is dirty. Defaults to `false`. */
	showChangedFields?: boolean
	/** Informs all underlying fields to show the optional hint for non-required fields. Defaults to `true`. */
	showOptionalHint?: boolean
	/** Allows specifying what a auto-completer such as a password manager. Set this to `"off"` to disable the forms autocomplete. */
	autoComplete?: string

	/** The props that will be set on the HTML `<form>` tag. Useful for handling key presses or other native events. */
	htmlFormProps?: JSX.IntrinsicElements['form']

	/** If you want to submit programmatically, use the `handleSubmit` from `render()` and set this to `() => undefined` */
	onSubmit: (fields: T, event: BaseSyntheticEvent | undefined, methods: FormMethods<T>) => Awaitable<any>
	/** Helper callback for listening to field changes and acting upon them. */
	onChange?: (fields: DeepPartial<T>, methods: FormMethods<T>) => void
	/** This is where you will place all your fields. That way, the form has the right context.
	 * Returns all available functions from `useForm` and some extra utility functions. */
	render: (fields: FormRenderFields<T>, methods: FormMethods<T>) => ReactNode

	style?: CSSProperties
	className?: string
}

/**
 * A simple `<form>` component using react-hook-form. Accepts a `onSubmit` function and a `render` function
 * which returns the `register` function to register the individual fields by name.
 * @example
 * ```tsx
 * type Model = {
 * 	text: string,
 * 	number: number,
 * }
 *
 * <Form<Model>
 * 	onSubmit={(fields) => console.log(fields)}
 * 	render={({ fieldProps, name }) => (
 * 		<>
 * 			<input {...fieldProps('text')} />
 * 			<Input name={name('number')} type='number' label='Number' required />
 * 			<button type='submit'>Submit</button>
 * 		</>
 * 	)}
 * />
 * ```
 */
export const Form = forwardRef(
	<T extends FieldValues>(
		{
			render,
			onSubmit,
			schema,
			autoComplete,
			validationMode = 'onSubmit',
			reValidateMode = 'onChange',
			onChange,
			initialValues,
			criteriaMode,
			shouldFocusError = true,
			isNativeValidation = false,
			style,
			className,
			showChangedFields = false,
			showOptionalHint = true,
			htmlFormProps = {},
		}: FormProps<T>,
		ref?: ForwardedRef<FormMethods<T>>,
	) => {
		const defaultValues = useMemo(() => {
			if (initialValues !== undefined) {
				const cleanedValues = {} as DefaultValues<T>
				Object.entries(initialValues).forEach(([key, value]) => {
					set(cleanedValues, key, value)
				})
				return cleanedValues
			}
			return undefined
		}, [initialValues])

		const methods = useForm<T>({
			resolver: schema ? superstructResolver(schema) : undefined,
			shouldUseNativeValidation: isNativeValidation,
			mode: validationMode,
			reValidateMode,
			criteriaMode,
			shouldFocusError,
			// We allow setting initialValues to a string path, e.g. `'my.field.path': any` which means we have to convert them
			// before setting them as default values to avoid duplicate values of paths and objects
			defaultValues,
		})

		/**
		 * RHF mutates the current form values when we clear the form, but we still want `reset()`
		 * without arguments to restore the original `initialValues`.
		 *
		 * To keep those two behaviors separate:
		 * - `clearFields()` supplies an explicit cleared payload to `reset(...)`
		 * - bare `reset()` is wrapped to always re-apply the original default values
		 *
		 * We also sync named DOM elements after every reset because some native inputs are cleared
		 * manually during `clearFields()` and otherwise can remain visually stale.
		 */
		const reset = useCallback<UseFormReturn<T, object>['reset']>(
			(values, options) => {
				const nextValues = values === undefined ? defaultValues : values
				if (values === undefined) {
					methods.reset(defaultValues, { ...options, keepDefaultValues: true })
				} else {
					methods.reset(values, options)
				}
				requestAnimationFrame(() => syncNamedElements(nextValues))
			},
			[defaultValues, methods],
		)

		const { handleSubmit, watch } = methods

		/**
		 * Clear all mounted fields without destroying the form's original reset target.
		 *
		 * A plain `reset({})` is not enough here because RHF treats missing values as "fall back to
		 * default values", which would immediately bring `initialValues` back for controlled selects.
		 * `clearInputs(methods)` instead produces an explicit empty value for every registered field path.
		 */
		const clearFields = useCallback(() => {
			reset(clearInputs(methods) as DefaultValues<T>)
		}, [methods, reset])

		useEffect(() => {
			if (!onChange) return

			return watch((values) => onChange(sanitizeFields(values), { ...methods, reset, clearFields })).unsubscribe
		}, [watch, onChange, clearFields, methods, reset])

		useImperativeHandle(ref, () => ({ ...methods, reset, clearFields }), [methods, reset, clearFields])

		const fields = useMemo(() => {
			if (schema?.schema) {
				const test = Object.entries<Struct<any, any>>(schema.schema).map(([fieldName, struct]) => [
					fieldName,
					{
						name: fieldName,
						isRequired: !struct.type.includes('Optional'),
						type: inferFieldType(struct),
					},
				])
				return Object.fromEntries(test)
			}
			return {}
		}, [schema])

		return (
			<FormProvider<T> {...methods}>
				<FormOptionsProvider showChangedFields={showChangedFields} showOptionalHint={showOptionalHint}>
					<form
						{...htmlFormProps}
						noValidate={!isNativeValidation}
						autoComplete={autoComplete}
						style={style}
						className={className}
						onSubmit={handleSubmit(async (fields, event) => {
							const sanitizedFields = sanitizeFields(fields)
							await onSubmit(sanitizedFields as T, event, { ...methods, reset, clearFields })
							event?.preventDefault()
						})}
					>
						{render(fields, { ...methods, reset, clearFields })}
					</form>
				</FormOptionsProvider>
			</FormProvider>
		)
	},
) as <T extends FieldValues>(props: FormProps<T> & { ref?: React.ForwardedRef<FormMethods<T>> }) => JSX.Element

export { sanitizeFields }
